import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MapPin, MagnifyingGlass, X, Storefront, ClockCounterClockwise, Trash } from "@phosphor-icons/react";
import {
  refreshAddressHistory,
  getCachedAddressHistory,
  filterCachedAddressHistory,
  pushAddressHistory,
  removeAddressHistory,
} from "@/lib/addressHistory";

/**
 * Autocomplete search for a business / address via Photon (OSM).
 *
 * On selection, calls `onPick(place)` with:
 *   { name, address, postal_code, city, latitude, longitude, label }
 *
 * Also shows the last 10 previously-picked addresses (shared with the team via
 * the backend `/api/address-history` endpoint, cached in localStorage) at the
 * top of the dropdown for instant reuse.
 */
export default function PlacePicker({ value, onChange, onPick, placeholder = "Rechercher un commerce ou une adresse…", testid = "place-picker" }) {
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState(() => getCachedAddressHistory());
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  // Fetch fresh team history from server on mount
  useEffect(() => {
    let cancelled = false;
    refreshAddressHistory().then(() => { if (!cancelled) setHistory(filterCachedAddressHistory(value || "")); });
    return () => { cancelled = true; };
  }, []);   

  // Filter history live on input change
  useEffect(() => {
    setHistory(filterCachedAddressHistory(value || ""));
  }, [value, open]);

  // Debounced remote search
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/places/search", { params: { q, limit: 6 } });
        setResults(r.data || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const pick = async (r) => {
    setOpen(false);
    onPick?.(r);
    await pushAddressHistory(r);
    setHistory(filterCachedAddressHistory(""));
  };

  const removeHist = async (e, addr) => {
    e.stopPropagation();
    await removeAddressHistory(addr);
    setHistory(filterCachedAddressHistory(value || ""));
  };

  const items = [
    ...history.map((h) => ({ ...h, __source: "history" })),
    ...results.map((r) => ({ ...r, __source: "remote" })),
  ];

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showHistorySection = open && history.length > 0;
  const showRemoteSection = open && ((value || "").trim().length >= 3);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        <input
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-md pl-9 pr-9 h-10 text-sm outline-none focus:border-[#005CFF]/50 text-white placeholder:text-white/40"
          data-testid={`${testid}-input`}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange(""); setResults([]); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (showHistorySection || showRemoteSection) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-neutral-950/95 backdrop-blur-md border border-white/15 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto" data-testid={`${testid}-list`}>
          {/* Team history section */}
          {showHistorySection && (
            <>
              <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-white/40 bg-white/[0.02] flex items-center gap-1.5 border-b border-white/5">
                <ClockCounterClockwise size={10} weight="bold" /> Récents équipe · {history.length}
              </div>
              {history.map((r, i) => {
                const idx = i;
                return (
                  <button
                    key={`hist-${r.address}-${i}`}
                    type="button"
                    onClick={() => pick(r)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/5 last:border-0 transition-colors relative group ${highlight === idx ? "bg-[#005CFF]/25" : "hover:bg-white/[0.04]"}`}
                    data-testid={`${testid}-hist-${i}`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5">
                      <ClockCounterClockwise size={12} className="text-emerald-400" weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1 pr-6">
                      <div className="text-sm font-medium truncate">{r.name || r.address}</div>
                      <div className="text-[10px] text-white/50 truncate">{r.address}</div>
                      {r.postal_code && (
                        <div className="text-[9px] text-white/40 mt-0.5">{r.postal_code} {r.city}</div>
                      )}
                    </div>
                    <div
                      onClick={(e) => removeHist(e, r.address)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400 p-1 cursor-pointer"
                      title="Retirer de l'historique"
                      data-testid={`${testid}-hist-del-${i}`}
                    >
                      <Trash size={11} />
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* Remote section */}
          {showRemoteSection && (
            <>
              {showHistorySection && results.length > 0 && (
                <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-white/40 bg-white/[0.02] flex items-center gap-1.5 border-b border-white/5">
                  <MagnifyingGlass size={10} weight="bold" /> Recherche
                </div>
              )}
              {loading && (
                <div className="p-3 text-xs text-white/50 text-center">Recherche…</div>
              )}
              {!loading && results.length === 0 && (value || "").trim().length >= 3 && (
                <div className="p-3 text-xs text-white/50 text-center">
                  {history.length === 0 ? "Aucun résultat — continue à taper" : "Aucun autre résultat"}
                </div>
              )}
              {results.map((r, i) => {
                const idx = history.length + i;
                return (
                  <button
                    key={`${r.osm_id}-${i}`}
                    type="button"
                    onClick={() => pick(r)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/5 last:border-0 transition-colors ${highlight === idx ? "bg-[#005CFF]/25" : "hover:bg-white/[0.04]"}`}
                    data-testid={`${testid}-item-${i}`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-[#005CFF]/20 flex items-center justify-center shrink-0 mt-0.5">
                      {r.category === "shop" || r.category === "amenity"
                        ? <Storefront size={13} className="text-[#7CB2FF]" weight="duotone" />
                        : <MapPin size={13} className="text-[#7CB2FF]" weight="duotone" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.name || r.label.split(",")[0]}</div>
                      <div className="text-[10px] text-white/50 truncate">{r.address}</div>
                      {r.postal_code && (
                        <div className="text-[9px] text-white/40 mt-0.5">
                          {r.postal_code} {r.city} · {r.latitude?.toFixed(4)}, {r.longitude?.toFixed(4)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
